/**
 * OAuthCallback.jsx
 *
 * This page is loaded INSIDE the popup window after Google/Facebook
 * redirects back to React (localhost:3000/oauth-callback#token=...&...).
 *
 * Because the popup is now same-origin as the parent (:3000),
 * BroadcastChannel works perfectly to pass auth data to LoginModal.
 *
 * HOW TO USE:
 *   Add a route in your App.jsx / router for path "/oauth-callback"
 *   that renders this component.
 *
 *   React Router example:
 *     <Route path="/oauth-callback" element={<OAuthCallback />} />
 *
 *   Without React Router (hash-based routing in App.jsx) — add this
 *   check at the TOP of AppContent, before any other rendering:
 *
 *     if (window.location.pathname === '/oauth-callback') {
 *       return <OAuthCallback />;
 *     }
 */

import React, { useEffect, useState } from 'react';

const OAuthCallback = () => {
  const [status, setStatus] = useState('Processing login...');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    // Read data from URL hash (e.g. #token=abc&email=x@y.com&...)
    const hash = window.location.hash.slice(1); // remove leading '#'
    const params = new URLSearchParams(hash);

    const error    = params.get('error');
    const token    = params.get('token');
    const userId   = params.get('user_id');
    const email    = params.get('email');
    const name     = params.get('name');
    const isAdmin  = params.get('is_admin') === 'True' || params.get('is_admin') === 'true';

    // Use BroadcastChannel so the parent tab receives the message
    // even though window.opener is null cross-origin
    const channel = new BroadcastChannel('oauth_channel');

    if (error) {
      console.error('❌ OAuth error:', error);
      setIsError(true);
      setStatus(`Login failed: ${decodeURIComponent(error)}`);

      channel.postMessage({ type: 'oauth-error', error: decodeURIComponent(error) });
      channel.close();

      setTimeout(() => window.close(), 2500);

    } else if (token) {
      console.log('✅ OAuth success, broadcasting to parent...');
      setStatus('Login successful! Closing window...');

      const message = {
        type: 'oauth-success',
        token,
        user: { id: userId, email, name, is_admin: isAdmin, isAdmin },
        isAdmin,
      };

      channel.postMessage(message);
      channel.close();

      setTimeout(() => window.close(), 1200);

    } else {
      setIsError(true);
      setStatus('No token received. Something went wrong.');
      channel.postMessage({ type: 'oauth-error', error: 'No authentication token received' });
      channel.close();
      setTimeout(() => window.close(), 3000);
    }
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '48px 40px',
        textAlign: 'center',
        maxWidth: '360px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Spinner or checkmark */}
        {!isError ? (
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width="36" height="36" viewBox="0 0 52 52" fill="none"
              stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 27l8 8 16-16"/>
            </svg>
          </div>
        ) : (
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width="36" height="36" viewBox="0 0 52 52" fill="none"
              stroke="white" strokeWidth="4" strokeLinecap="round">
              <path d="M16 16l20 20M36 16L16 36"/>
            </svg>
          </div>
        )}

        <h2 style={{ color: '#111827', marginBottom: 12, fontSize: 20, fontWeight: 700 }}>
          {isError ? 'Login Failed' : 'Login Successful!'}
        </h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>{status}</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
